import React, { useMemo, useState } from 'react';
import {
    Paper,
    Box,
    Typography,
    Table,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    TableContainer,
    Chip,
    IconButton,
    Tooltip,
    TextField,
    MenuItem,
    Stack,
    CircularProgress,
    Pagination,
    Alert,
} from '@mui/material';
import { Replay as ReplayIcon, Refresh } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';

import { DELIVERY_STATUS_META } from './webhookConstants';
import { useGetWebhookDeliveries } from '../../hooks/webhooks/useGetWebhookDeliveries';
import { useReplayWebhookDelivery } from '../../hooks/webhooks/useReplayWebhookDelivery';

const STATUS_FILTER_OPTIONS = [
    { value: '',          labelEn: 'All',        labelAr: 'الكل' },
    { value: 'pending',   labelEn: 'Pending',    labelAr: 'قيد الإرسال' },
    { value: 'delivered', labelEn: 'Delivered',  labelAr: 'تم التسليم' },
    { value: 'failed',    labelEn: 'Failed',     labelAr: 'فشل' },
    { value: 'abandoned', labelEn: 'Abandoned',  labelAr: 'تم التخلي' },
];

const WebhookDeliveriesTable = ({ webhookId }) => {
    const { i18n } = useTranslation();
    const isArabic = (i18n.language || 'en').startsWith('ar');

    const [page, setPage] = useState(1);
    const [statusFilter, setStatusFilter] = useState('');

    const params = useMemo(
        () => ({
            page,
            per_page: 50,
            status: statusFilter || undefined,
        }),
        [page, statusFilter]
    );

    const { deliveries, meta, isLoading, error, refetch } = useGetWebhookDeliveries(webhookId, params);
    const { replayDeliveryMutate, isReplaying } = useReplayWebhookDelivery();

    const totalPages = meta?.last_page || Math.max(1, Math.ceil((meta?.total || 0) / (meta?.per_page || 50)));

    const handleReplay = (deliveryId) => {
        replayDeliveryMutate(deliveryId, {
            onSuccess: () => refetch(),
        });
    };

    return (
        <Paper
            elevation={0}
            sx={{
                p: { xs: 2, md: 3 },
                borderRadius: 3,
                border: (t) => `1px solid ${t.palette.divider}`,
            }}
        >
            <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={1.5}
                alignItems={{ xs: 'stretch', sm: 'center' }}
                justifyContent="space-between"
                sx={{ mb: 2 }}
            >
                <Box>
                    <Typography variant="h6" fontWeight={700}>
                        {isArabic ? 'سجل المحاولات' : 'Delivery log'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        {isArabic
                            ? 'آخر المحاولات لكل حدث. يمكنك إعادة المحاولة للمحاولات الفاشلة.'
                            : 'Recent attempts per event. Failed/abandoned rows can be replayed.'}
                    </Typography>
                </Box>

                <Stack direction="row" spacing={1} alignItems="center">
                    <TextField
                        select
                        size="small"
                        label={isArabic ? 'الحالة' : 'Status'}
                        value={statusFilter}
                        onChange={(e) => {
                            setStatusFilter(e.target.value);
                            setPage(1);
                        }}
                        sx={{ minWidth: 160 }}
                    >
                        {STATUS_FILTER_OPTIONS.map((opt) => (
                            <MenuItem key={opt.value || 'all'} value={opt.value}>
                                {isArabic ? opt.labelAr : opt.labelEn}
                            </MenuItem>
                        ))}
                    </TextField>

                    <Tooltip title={isArabic ? 'تحديث' : 'Refresh'}>
                        <IconButton onClick={() => refetch()} size="small">
                            <Refresh />
                        </IconButton>
                    </Tooltip>
                </Stack>
            </Stack>

            {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {isArabic ? 'تعذر تحميل سجل المحاولات' : 'Failed to load delivery log'}
                </Alert>
            )}

            {isLoading ? (
                <Box sx={{ p: 3, textAlign: 'center' }}>
                    <CircularProgress size={24} />
                </Box>
            ) : deliveries.length === 0 ? (
                <Box sx={{ p: 4, textAlign: 'center' }}>
                    <Typography variant="body2" color="text.secondary">
                        {isArabic ? 'لا توجد محاولات تطابق هذا الفلتر.' : 'No deliveries match this filter.'}
                    </Typography>
                </Box>
            ) : (
                <>
                    <TableContainer>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>{isArabic ? 'الحدث' : 'Event'}</TableCell>
                                    <TableCell align="center">{isArabic ? 'الحالة' : 'Status'}</TableCell>
                                    <TableCell align="center">{isArabic ? 'كود HTTP' : 'HTTP Code'}</TableCell>
                                    <TableCell align="center">{isArabic ? 'المحاولة' : 'Attempt'}</TableCell>
                                    <TableCell align="center">{isArabic ? 'المحاولة التالية' : 'Next attempt'}</TableCell>
                                    <TableCell align="center">{isArabic ? 'تم التسليم' : 'Delivered at'}</TableCell>
                                    <TableCell align="center">{isArabic ? 'الإجراءات' : 'Actions'}</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {deliveries.map((d) => {
                                    const meta = DELIVERY_STATUS_META[d.status] || {
                                        color: 'default',
                                        labelEn: d.status || '-',
                                        labelAr: d.status || '-',
                                    };
                                    const canReplay = d.status === 'failed' || d.status === 'abandoned';

                                    return (
                                        <TableRow key={d.id} hover>
                                            <TableCell>
                                                <Typography
                                                    variant="caption"
                                                    sx={{ fontFamily: 'monospace', fontWeight: 600 }}
                                                >
                                                    {d.event}
                                                </Typography>
                                            </TableCell>
                                            <TableCell align="center">
                                                <Chip
                                                    label={isArabic ? meta.labelAr : meta.labelEn}
                                                    size="small"
                                                    color={meta.color}
                                                    variant="tonal"
                                                    sx={{ fontWeight: 'bold', borderRadius: '6px', minWidth: 80 }}
                                                />
                                            </TableCell>
                                            <TableCell align="center">
                                                {d.response_code ?? '—'}
                                            </TableCell>
                                            <TableCell align="center">
                                                {d.attempt ?? 0}
                                            </TableCell>
                                            <TableCell align="center">
                                                <Typography variant="caption" sx={{ whiteSpace: 'nowrap' }}>
                                                    {d.next_attempt_at
                                                        ? dayjs(d.next_attempt_at).format('YYYY-MM-DD HH:mm')
                                                        : '—'}
                                                </Typography>
                                            </TableCell>
                                            <TableCell align="center">
                                                <Typography variant="caption" sx={{ whiteSpace: 'nowrap' }}>
                                                    {d.delivered_at
                                                        ? dayjs(d.delivered_at).format('YYYY-MM-DD HH:mm')
                                                        : '—'}
                                                </Typography>
                                            </TableCell>
                                            <TableCell align="center">
                                                {canReplay ? (
                                                    <Tooltip title={isArabic ? 'إعادة المحاولة' : 'Replay'}>
                                                        <span>
                                                            <IconButton
                                                                size="small"
                                                                onClick={() => handleReplay(d.id)}
                                                                disabled={isReplaying}
                                                                color="primary"
                                                            >
                                                                <ReplayIcon fontSize="small" />
                                                            </IconButton>
                                                        </span>
                                                    </Tooltip>
                                                ) : (
                                                    <Typography variant="caption" color="text.disabled">—</Typography>
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    {totalPages > 1 && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                            <Pagination
                                count={totalPages}
                                page={page}
                                onChange={(_e, value) => setPage(value)}
                                size="small"
                                color="primary"
                            />
                        </Box>
                    )}
                </>
            )}
        </Paper>
    );
};

export default WebhookDeliveriesTable;
